import { ANALYZE_STATUS } from '@deepcloud/api-deep-box'
import { useDeepBoxDeepBoxesBoxesNodesStore } from '@/stores/deepbox/deepboxes/boxes/nodes'
import { useApi } from '@/composables/api.ts'
import type {
  Box,
  BoxInfo,
  CreateNodesAnalyze,
  DeepBox,
  Node,
} from '@deepcloud/api-deep-box'
import type { RouteLocation } from 'vue-router'

export const useDeepBoxCoreStore = defineStore('deepBoxCore', () => {
  const types = ref<DeepBox[]>([])
  const type = ref<DeepBox | undefined>()
  const selectedBox = ref<Box | BoxInfo>()
  const deleteTrashPending = ref(false)
  const fetchTypePending = ref(false)
  const selectedSearchLocation = ref(null)
  const mobileSearchField = ref('')
  // this state can be set from some box action
  // is used for ex: to set the "loading" prop on NodeDataTable to avoid the infinite-scroll
  // if the action is not completed. For ex: when deleting or moving files
  const isBoxActionLoading = ref(false)

  const { deepBoxAPI } = useApi()

  async function fetchType(deepBoxNodeId: string) {
    fetchTypePending.value = true
    try {
      const res = await deepBoxAPI.value.deepBoxes.getById(deepBoxNodeId)
      type.value = { ...res.data }
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    } finally {
      fetchTypePending.value = false
    }
  }

  async function deleteTrash(route: RouteLocation) {
    deleteTrashPending.value = true
    try {
      const res = await deepBoxAPI.value.deepBoxesBoxesTrash.delete(
        route.params.type as string,
        route.params.box as string,
      )
      const deepBoxDeepBoxesBoxesNodesStore =
        useDeepBoxDeepBoxesBoxesNodesStore()
      if (deepBoxDeepBoxesBoxesNodesStore.data) {
        deepBoxDeepBoxesBoxesNodesStore.data = {
          ...deepBoxDeepBoxesBoxesNodesStore.data,
          size: 0,
          nodes: [],
        }
      }
      return Promise.resolve(res)
    } catch (error) {
      return Promise.reject(error)
    } finally {
      deleteTrashPending.value = true
    }
  }

  async function analyzeNode(
    payload: Node,
    params: CreateNodesAnalyze = { waitForResult: 0 },
  ) {
    // prevent to start the analysis when the node do not have the policy to do it
    if (!payload?.policy?.canAnalyze) return
    // call the analysis action
    const node = {
      ...payload,
      analysisStatus: ANALYZE_STATUS.ANALYSIS_STATUS_RUNNING,
    }

    const deepBoxDeepBoxesBoxesNodesStore = useDeepBoxDeepBoxesBoxesNodesStore()

    if (deepBoxDeepBoxesBoxesNodesStore.data?.nodes) {
      const nodes = [...deepBoxDeepBoxesBoxesNodesStore.data.nodes]
      const nodeIndex = nodes?.findIndex((n) => n.nodeId === node.nodeId)
      if (nodeIndex !== -1) {
        const n = nodes[nodeIndex]
        nodes[nodeIndex] = { ...n, ...node }
      }
      deepBoxDeepBoxesBoxesNodesStore.data.nodes = nodes
    }

    await deepBoxAPI.value.nodesAnalyze.create(payload.nodeId, params)
  }

  return {
    // states
    types,
    type,
    selectedBox,
    deleteTrashPending,
    fetchTypePending,
    selectedSearchLocation,
    mobileSearchField,
    // actions
    fetchType,
    deleteTrash,
    analyzeNode,
    isBoxActionLoading,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDeepBoxCoreStore, import.meta.hot))
}
